import { useCallback } from 'react';

import { useLocalStorageState } from '@/hooks/use-local-storage-state';

import type { SourcingPurchaseDeactivationReason } from 'api-clients/monolith';

const SOURCE_AUTOMATION_BANNER_STORAGE_KEY = 'source-automation-banner';
const MAX_DISMISSAL_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_OPENINGS = 200;

/**
 * `target_budget_breached` is a frontend-only reason synthesised when spend
 * crosses the threshold but the backend has not yet deactivated the channel.
 */
export type AutomationReason =
  | SourcingPurchaseDeactivationReason
  | 'target_budget_breached';

/**
 * Each dismissed reason stores a timestamp (ms) instead of a bare boolean
 * so stale entries can be garbage-collected.
 */
export type DismissalsByReason = Record<string, number>;
export type AutomationBannerDismissalTree = Record<string, DismissalsByReason>;

/**
 * Build a composite dismissal key from the automation reason and active state.
 * `automated_as_goal_met` produces different banners depending on whether the
 * channel is active (resumed) or paused, so we namespace the key accordingly.
 */
export function dismissalKey(
  reason: AutomationReason | null | undefined,
  isActive: boolean,
): string | null {
  if (reason == null) return null;
  if (reason === 'automated_as_goal_met') {
    return isActive
      ? 'automated_as_goal_met:active'
      : 'automated_as_goal_met:paused';
  }
  if (reason === 'target_budget_met') {
    return isActive ? 'target_budget_met:active' : 'target_budget_met:paused';
  }
  return reason;
}

/**
 * Inverse keys: when the channel flips between active/paused the old
 * dismissal for `automated_as_goal_met` is stale and should be cleared.
 */
export function staleKeysForTransition(isActive: boolean): string[] {
  return isActive
    ? ['automated_as_goal_met:paused', 'hiring_goal_ended']
    : ['automated_as_goal_met:active', 'target_budget_met:active'];
}

/**
 * True if a dismissal timestamp is still within the TTL window.
 * Use at read time so stale dismissals stop hiding the banner even if no
 * write has occurred to trigger a purge.
 */
export function isDismissalActive(timestamp: number | undefined): boolean {
  if (timestamp == null) return false;
  return Date.now() - timestamp < MAX_DISMISSAL_AGE_MS;
}

/**
 * Drop dismissals older than the TTL and cap the tree at MAX_OPENINGS
 * (least recently dismissed first). Returns the input reference unchanged
 * when nothing needed pruning, so React can bail out of re-renders.
 */
function purgeStaleEntries(
  tree: AutomationBannerDismissalTree,
): AutomationBannerDismissalTree {
  const now = Date.now();
  let mutated = false;

  const prunedEntries = Object.entries(tree).flatMap<
    [string, DismissalsByReason]
  >(([slug, reasons]) => {
    const reasonEntries = Object.entries(reasons);
    const keptEntries = reasonEntries.filter(
      ([, ts]) => now - ts < MAX_DISMISSAL_AGE_MS,
    );

    if (keptEntries.length === 0) {
      if (reasonEntries.length > 0) mutated = true;
      return [];
    }

    const openingMutated = keptEntries.length !== reasonEntries.length;
    if (openingMutated) mutated = true;
    return [[slug, openingMutated ? Object.fromEntries(keptEntries) : reasons]];
  });

  if (prunedEntries.length > MAX_OPENINGS) {
    const sortedEntries = [...prunedEntries].sort(
      ([, a], [, b]) =>
        Math.max(...Object.values(b)) - Math.max(...Object.values(a)),
    );
    return Object.fromEntries(sortedEntries.slice(0, MAX_OPENINGS));
  }

  return mutated ? Object.fromEntries(prunedEntries) : tree;
}

export function omitAutomationBannerDismissals(
  prev: AutomationBannerDismissalTree,
  openingSlug: string,
  reasons: string[],
): AutomationBannerDismissalTree {
  const existing = prev[openingSlug];
  if (!existing) return prev;
  if (!reasons.some(r => r in existing)) return prev;

  const openingDismissals = Object.fromEntries(
    Object.entries(existing).filter(([reason]) => !reasons.includes(reason)),
  );

  if (Object.keys(openingDismissals).length === 0) {
    const { [openingSlug]: _removed, ...rest } = prev;
    return rest;
  }

  return { ...prev, [openingSlug]: openingDismissals };
}

export function useAutomationBannerState() {
  const [automationBannerClosed, setAutomationBannerClosedRaw] =
    useLocalStorageState<AutomationBannerDismissalTree>(
      SOURCE_AUTOMATION_BANNER_STORAGE_KEY,
      {},
    );

  const setAutomationBannerClosed = useCallback(
    (
      updater: (
        prev: AutomationBannerDismissalTree,
      ) => AutomationBannerDismissalTree,
    ) => {
      setAutomationBannerClosedRaw(prev => {
        const next = updater(prev);
        return purgeStaleEntries(next);
      });
    },
    [setAutomationBannerClosedRaw],
  );

  return {
    automationBannerClosed,
    setAutomationBannerClosed,
  } as const;
}
